import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, resetAuthState } from "../redux/features/loginSlice";
import PasswordField from "../field/PasswordField";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";
import { useTranslation } from "react-i18next";
import { useModal } from "../hooks/ModalContext";

const LoginModal = ({ isActive, onClose, onForgotPassword, onRegister, onVerifyOtp }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();
  const { status, error, emailVerify } = useSelector((state) => state.auth);
  const { setEmailForOtp } = useModal();

  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // keep latest onClose in a ref so event handler uses the newest callback
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // initialize bootstrap modal instance and attach hidden.bs.modal listener once
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    const modalInstance = bootstrap.Modal.getOrCreateInstance(el);

    const handleHidden = () => {
      // When bootstrap hides the modal (backdrop click / Esc / hide()), notify parent
      onCloseRef.current?.();
    };

    el.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      el.removeEventListener("hidden.bs.modal", handleHidden);
      // dispose bootstrap instance on unmount to avoid stale instances
      const inst = bootstrap.Modal.getInstance(el);
      inst?.dispose();
    };
  }, []); // run once on mount

  // show/hide modal when `isActive` changes
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const modalInstance = bootstrap.Modal.getOrCreateInstance(el);

    if (isActive) {
      modalInstance.show();
    } else {
      modalInstance.hide();
    }
  }, [isActive]);

  // When login succeeds, hide via bootstrap instance (hidden event will call onClose)
  useEffect(() => {
    if (status === "succeeded" && modalRef.current) {
      const modalInstance = bootstrap.Modal.getInstance(modalRef.current);
      modalInstance?.hide();

      if (emailVerify === false) {
        setTimeout(() => {
          setEmailForOtp(email);
          onVerifyOtp?.();
          dispatch(resetAuthState());
        }, 250);
      }
    }
  }, [status, emailVerify, onVerifyOtp, dispatch, email]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(loginUser({ email, password }));
  };

  // helpers to switch flows: hide first, then call parent to open target modal
  const hideThen = (cb) => {
    const modalInstance = bootstrap.Modal.getInstance(modalRef.current);
    modalInstance?.hide();
    // wait a bit for hidden animation -> parent will get onClose via hidden event
    setTimeout(() => cb?.(), 250);
  };

  return (
    <div
      className="modal fade"
      ref={modalRef}
      tabIndex="-1"
      aria-hidden="true"
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("Welcome_back_to_Amzio")}</h5>
            {/* Close via bootstrap hide so hidden.bs.modal runs and keeps state in sync */}
            <button
              type="button"
              className="btn-close"
              onClick={() => bootstrap.Modal.getInstance(modalRef.current)?.hide()}
            />
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">{t("Email_address")}</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <PasswordField password={password} setPassword={setPassword} />

              <div className="mb-3 text-end">
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary"
                  onClick={() => hideThen(onForgotPassword)}
                >
                  {t("Forgot_Password?")}
                </button>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Logging in..." : "Login"}
              </button>

              {error && <p className="text-danger mt-2">{error}</p>}
            </form>

            <p className="mt-3 text-center">
              {t("Don't_have_an_account?")}{" "}
              <button
                className="btn btn-link p-0"
                onClick={() => hideThen(onRegister)}
              >
                {t("Register")}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
